import { useState } from 'react';
import { NoteAdd } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { BatchUploadType, useCreateBatchMutation, useCreateInvoiceMutation } from '../../api/gql';
import { BatchUploadProcessingModal } from '../../components/BatchUploadProcessingModal';
import {
  ALLOWED_FILES,
  FileDropzone,
  SUPPORTED_MIME_TYPES,
  SUPPORTED_MIME_TYPES_FOR_BATCH_UPLOAD,
  SUPPORTED_MIME_TYPES_FOR_MASS_UPLOAD,
} from '../../components/FileDropzone';
import { PageHeader } from '../../components/PageHeader';
import { useXmlDataStore } from '../../hooks/useXmlDataStore';
import { PageContent, PageLayout } from '../../layout';
import { XmlInvoiceType } from '../../models/xml/invoice';
import { ScenarioSelectInputInfoBox } from '../../modules/scenarios/ScenariosSelectInput';
import { getConfig } from '../../utils/config';
import { notify } from '../../utils/notification';
import { SuccessResult, useFileUpload } from './hooks/useFileUpload';
import { useSafeNavigation } from '../../hooks/useSafeNavigation';

const { uploadSizeLimit, stormwareXmlFormatUrl } = getConfig();

function hasId(res: SuccessResult): res is Extract<SuccessResult, { id: string }> {
  return (res as Extract<SuccessResult, { id: string }>).id !== undefined;
}

export const FileUpload = () => {
  const { safeNavigate } = useSafeNavigation();
  const { t } = useTranslation('page-upload');
  const { createXmlDataModel } = useXmlDataStore();

  const [scenarioId, setScenarioId] = useState('');

  const { mutate: createInvoiceMutate, isPending: isCreateInvoiceLoading } = useCreateInvoiceMutation({
    onSuccess(res) {
      safeNavigate('/new/invoice/' + res.createInvoice.id);
    },
    onError(err) {
      console.error(err);
      notify.negative({ message: t('error.default') });
    },
  });

  const { mutateAsync } = useCreateBatchMutation();
  const { uploadFile, uploadUnsupportedFile, uploadSpecifiedFormatFile, uploadBatch, state } = useFileUpload();

  const onFileUpload = (res: SuccessResult) => {
    if (hasId(res)) {
      safeNavigate(`/upload/invoice/${res.id}`);
      return;
    }

    throw new Error('Upload returned unexpected result');
  };

  const onUnsupportedFileUpload = (res: SuccessResult) => {
    if (hasId(res)) {
      safeNavigate(`/upload/unsupported/${res.id}`);
      return;
    }

    throw new Error('Upload returned unexpected result');
  };

  const onBatchUploadSuccess = (res: SuccessResult) => {
    if (hasId(res)) {
      return;
    }

    createXmlDataModel(res.invoices as XmlInvoiceType[], res.scenarioId);
    safeNavigate(res.claims ? '/upload/claims/summary' : '/upload/xml/summary');
    return;
  };

  const onUploadError = (res: { status: number }) => {
    switch (res.status) {
      case 400:
        notify.negative({
          message: t('error.badRequest'),
          duration: 10000,
          alertProps: {
            action: (
              <Button
                // component cannot be Link because Toaster is outside the react-router context
                component={'a'}
                href={stormwareXmlFormatUrl}
                target={'_blank'}
                color={'inherit'}
                size={'small'}
              >
                {t('error.badRequestAction')}
              </Button>
            ),
          },
        });
        break;
      case 412:
        notify.negative({
          message: t('error.preconditionFailed'),
          duration: 15000,
        });
        break;
      case 413:
        notify.negative({
          message: t('error.generalFileSizeError', {
            limit: Math.round((uploadSizeLimit / 1048576) * 100) / 100,
          }),
        });
        break;
      default:
        notify.negative({ message: t('error.default') });
    }
  };

  const handleFile = async (files: File[]) => {
    const uploadSize = files.reduce((acc, file) => acc + file.size, 0);

    // max upload size check
    if (uploadSize > uploadSizeLimit) {
      notify.negative({
        message: t('fileSizeError', {
          size: Math.round((uploadSize / 1048576) * 100) / 100,
          limit: Math.round((uploadSizeLimit / 1048576) * 100) / 100,
        }),
      });
      return;
    }

    const isMultipleUpload = files.length > 1;

    if (isMultipleUpload) {
      const includesUnsupportedFileForMassUpload = files.some(
        (file) => !SUPPORTED_MIME_TYPES_FOR_MASS_UPLOAD.includes(file.type),
      );
      // handle case when there is multiple files but one of them is not supported
      if (includesUnsupportedFileForMassUpload) {
        notify.negative({
          message: t('error.badFormatAmongOthers'),
        });
        return;
      }

      try {
        const res = await mutateAsync({ input: { type: BatchUploadType.Pdf } });
        await uploadBatch({
          batchUploadId: res.createBatchUpload.id,
          files,
          onError: onUploadError,
          onSuccess() {
            safeNavigate(`/upload/batch/${res.createBatchUpload.id}`);
          },
        });
      } catch (e) {
        console.error(e);
        notify.negative({ message: t('error.default') });
      }

      return;
    }

    //flow for single file
    const file = files[0];
    if (!SUPPORTED_MIME_TYPES.includes(file.type)) {
      await uploadUnsupportedFile({
        file,
        onError: onUploadError,
        onSuccess: onUnsupportedFileUpload,
      });
      return;
    }

    // flow for batch upload
    if (SUPPORTED_MIME_TYPES_FOR_BATCH_UPLOAD.includes(file.type)) {
      // throttle the upload promise resolve to let user see the loading information
      const res = await uploadSpecifiedFormatFile({
        file,
        onSuccess: onBatchUploadSuccess,
        onError(res) {
          // omit 400 from default error handling as it is handled as unsupported file flow
          if (res.status === 400) {
            return;
          }
          onUploadError(res);
        },
        format: ALLOWED_FILES.find((allowedFiles) => allowedFiles.name === 'xml')?.mime.includes(file.type)
          ? 'xml'
          : 'xls',
        minDuration: 500,
        scenarioId,
      });

      // status 400 is handled as unsupported file flow
      if (res.status === 400) {
        await uploadUnsupportedFile({
          file,
          onError: onUploadError,
          onSuccess: onUnsupportedFileUpload,
        });
      }
      return;
    }

    await uploadFile({
      file,
      onSuccess: onFileUpload,
      onError: onUploadError,
      scenarioId,
    });
  };

  return (
    <>
      <PageLayout>
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          action={
            <LoadingButton
              startIcon={<NoteAdd />}
              onClick={() => createInvoiceMutate({})}
              loading={isCreateInvoiceLoading}
              variant={'outlined'}
              size={'large'}
              sx={(theme) => ({ bgcolor: theme.palette.common.white })}
            >
              {t('createNewInvoice')}
            </LoadingButton>
          }
        />
        <PageContent sx={{ alignItems: { xs: 'flex-start', md: 'center' } }}>
          <Stack
            flex={1}
            direction={{ sm: 'column' }}
            height={{ xs: 'auto', md: '100%' }}
            justifyContent={'center'}
            alignItems={'center'}
            width={'100%'}
            gap={{ xs: 2, md: 4 }}
          >
            <ScenarioSelectInputInfoBox onScenarioSelected={setScenarioId} />
            <Box
              flex={1}
              height={'100%'}
              width={'100%'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              maxHeight={{ md: 512 }}
            >
              <FileDropzone loading={state.loading} onFileUpload={handleFile} />
            </Box>
          </Stack>
        </PageContent>
      </PageLayout>
      <BatchUploadProcessingModal open={state.batchLoading} />
      {/*Outlet for modal pages*/}
      <Outlet />
    </>
  );
};
